/**
 * Doña Blanca Website - Unified Form Submission Service
 * Handles dispatching forms to Firebase Functions / Resend.
 */

export interface CommercialFormData {
  empresa: string;
  nombre: string;
  correo: string;
  telefono?: string;
  ciudad?: string;
  personas?: string;
  ubicaciones?: string;
  tipoServicio?: string;
  mensaje?: string;
  _hp?: string;
}

export interface ContactFormData {
  nombre: string;
  empresa?: string;
  correo: string;
  telefono?: string;
  motivo: string;
  mensaje: string;
  _hp?: string;
}

export interface BillingFormData {
  razonSocial: string;
  rfc: string;
  regimenFiscal: string;
  codigoPostal: string;
  usoCfdi: string;
  correo: string;
  telefono?: string;
  sucursal: string;
  fechaConsumo: string;
  folioTicket: string;
  montoTotal: string;
  formaPago: string;
  notas?: string;
  ticketFile?: File | null;
  _hp?: string;
}

/**
 * Valida un teléfono mexicano opcional:
 * - Si está vacío o sólo espacios, es válido (campo opcional).
 * - Si contiene texto, debe ser un número de 10 dígitos (opcionalmente con prefijo +52 o 52).
 */
export function isValidMexicanPhone(phone?: string): boolean {
  if (!phone || !phone.trim()) return true;
  const digitsOnly = phone.replace(/[\s\-\(\)\.]/g, '');
  if (digitsOnly.startsWith('+52')) {
    return /^\d{10}$/.test(digitsOnly.slice(3));
  }
  if (digitsOnly.startsWith('52') && digitsOnly.length === 12) {
    return /^\d{10}$/.test(digitsOnly.slice(2));
  }
  return /^\d{10}$/.test(digitsOnly);
}

export interface SubmitResponse {
  success: boolean;
  messageId?: string;
  error?: string;
  code?: string;
}

const ENDPOINT = import.meta.env.VITE_WEBSITE_FORMS_ENDPOINT || '/api/v1/website/forms';

export const fileToBase64 = (
  file: File
): Promise<{ name: string; type: string; size: number; base64: string }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] || '';
      resolve({
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        base64,
      });
    };
    reader.onerror = (error) => reject(error);
  });
};

export async function submitCommercialForm(
  data: CommercialFormData
): Promise<SubmitResponse> {
  return sendPayload({
    type: 'commercial',
    ...data,
  });
}

export async function submitContactForm(
  data: ContactFormData
): Promise<SubmitResponse> {
  return sendPayload({
    type: 'contact',
    ...data,
  });
}

export async function submitBillingForm(
  data: BillingFormData
): Promise<SubmitResponse> {
  let ticketAttachment: { name: string; type: string; size: number; base64: string } | undefined;

  if (data.ticketFile) {
    if (data.ticketFile.size > 5 * 1024 * 1024) {
      return {
        success: false,
        code: 'FILE_TOO_LARGE',
        error: 'El archivo excede el tamaño máximo permitido de 5 MB.',
      };
    }
    ticketAttachment = await fileToBase64(data.ticketFile);
  }

  const { ticketFile, ...rest } = data;
  return sendPayload({
    type: 'billing',
    ...rest,
    ticketAttachment,
  });
}

async function sendPayload(payload: any): Promise<SubmitResponse> {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      return {
        success: false,
        code: 'SERVICE_UNAVAILABLE',
        error:
          'No se pudo procesar la solicitud en este momento. Por favor contáctanos directamente a dblanca.restaurant@gmail.com.',
      };
    }

    const data = await res.json().catch(() => null);

    if (!res.ok) {
      return {
        success: false,
        code: data?.code || 'SUBMISSION_ERROR',
        error:
          data?.error ||
          data?.message ||
          'Error al procesar la solicitud. Por favor intenta más tarde o escribe a dblanca.restaurant@gmail.com.',
      };
    }

    if (data?.success) {
      return {
        success: true,
        messageId: data.messageId,
      };
    }

    return {
      success: false,
      code: data?.code || 'SUBMISSION_FAILED',
      error:
        data?.error ||
        data?.message ||
        'No se pudo completar el envío. Por favor intenta nuevamente o escribe a dblanca.restaurant@gmail.com.',
    };
  } catch {
    return {
      success: false,
      code: 'NETWORK_ERROR',
      error:
        'Error de conexión. Por favor verifica tu red o contáctanos a dblanca.restaurant@gmail.com.',
    };
  }
}
